import { asBackendRegion, residencyForBackendRegion } from '@peekapak/common/lib/regionAndResidency';
import { dataResidencyManager } from './DataResidencyManager';
import { SupportedDataResidency, toSupportedResidency } from './residency';
import { getDataResidencyRuntime } from './runtime';

interface CognitoConfig {
  region: string; // AWS region for infrastructure
  userPoolId?: string; // USA configs spell it this way
  userPool?: string; // FRA configs spell it this way
  identityPool: string;
  clientId: string;
  userPoolArn?: string; // Optional, only present in ARE configs
  hostedUICognitoDomain?: string; // For OAuth/SSO
  hostedUISignInCallbackPath?: string; // For OAuth/SSO
  hostedUISignOutCallbackPath?: string; // For OAuth/SSO
}

// The residency Amplify is currently configured for, and the serialising chain
// that keeps concurrent callers from interleaving (see
// initializeAmplifyForDataResidency).
let configuredResidency: SupportedDataResidency | null = null;
let configureChain: Promise<void> = Promise.resolve();

// Amplify's marker for "this session began at the Cognito hosted UI". Only the
// USA pools have a hosted UI; the FRA pools have OAuth disabled on the app
// client (no flows, no callback or logout URLs, no identity providers).
const HOSTED_UI_SIGN_IN_MARKER = 'amplify-signin-with-hostedUI';

/**
 * Force Amplify's hosted-UI marker to 'false' for residencies with no hosted
 * UI. Two Amplify behaviours make this necessary: `Amplify.configure` merges
 * over its previous config and never drops `_oAuthHandler` once any residency
 * has configured OAuth, and the marker is never reset after a redirect-based
 * hosted-UI logout (the redirect leaves before the `signOut` event fires). So a
 * page that configured USA first and then switched to FRA would keep both the
 * handler and a stale 'true' marker, and `Auth.signOut` would redirect to a
 * hosted-UI `/logout` the FRA pool cannot serve. 'false' is the same value
 * Amplify itself writes on a native sign-in.
 *
 * Both storages are written because the marker lives in whichever storage the
 * active Amplify config selected, and the cookie attributes match the
 * `cookieStorage` config below so the write overwrites rather than shadows.
 */
function clearHostedUISignInMarker(): void {
  try {
    window.localStorage.setItem(HOSTED_UI_SIGN_IN_MARKER, 'false');
  } catch {
    // Private-mode browsers can reject localStorage writes; the cookie below is
    // the copy the cookieStorage config actually reads.
  }

  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${HOSTED_UI_SIGN_IN_MARKER}=false; domain=${window.location.hostname}; path=/; samesite=strict${secure}`;
}

/**
 * Apply the `region` query parameter carried by email links (registration
 * confirmation, new-password confirmation, password reset). The links are
 * minted by the Cognito custom-message Lambda co-located with the pool that
 * holds the account, so the region is authoritative: persist the derived
 * residency and reconfigure Amplify BEFORE any Cognito call, otherwise a
 * link opened on a fresh browser targets whatever pool the residency guess
 * picked (typically US). Links without the parameter (older emails) are a
 * no-op.
 */
export async function applyBackendRegionFromLink(regionParam: string | null | undefined): Promise<void> {
  const backendRegion = asBackendRegion(regionParam ?? undefined);
  if (!backendRegion) {
    return;
  }

  const dataResidency = toSupportedResidency(residencyForBackendRegion(backendRegion));
  await dataResidencyManager.persistDataResidency(dataResidency);
  await initializeAmplifyForDataResidency(dataResidency);
}

/**
 * OAuth and preauth run only against the USA pools - the FRA app client has no
 * hosted UI - so a callback landing while the session is on ARE is redirected
 * to USA and the choice persisted.
 */
function applyOAuthResidencyOverride(dataResidency: SupportedDataResidency): SupportedDataResidency {
  const isOAuthCallback =
    window.location.pathname.includes('/oauth/') &&
    (window.location.search.includes('code=') || window.location.search.includes('error='));
  const isPreAuthRoute = window.location.pathname.includes('/preauth/');

  if ((isOAuthCallback || isPreAuthRoute) && dataResidency === SupportedDataResidency.ARE) {
    console.log('🔄 OAuth callback or preauth route detected, using US configuration for OAuth processing');
    void dataResidencyManager.persistDataResidency(SupportedDataResidency.USA);
    return SupportedDataResidency.USA;
  }

  return dataResidency;
}

/**
 * Configure Amplify for a specific data residency.
 *
 * Calls are serialised through a single chain rather than gated on a plain
 * `configuredResidency === dataResidency` compare. That compare is not atomic
 * across the `await` inside the configure, so two concurrent callers for
 * different residencies could both pass it and race to `Amplify.configure` -
 * whichever settled last won, and the session could end up pointed at the wrong
 * Cognito pool. myPeekaville hit exactly that shape: its app bootstrap and its
 * session reactivation both configure, and the bootstrap was not awaited.
 *
 * Serialising makes a same-residency follower a no-op (it sees the marker set
 * by the call it waited on) and a different-residency follower apply cleanly
 * after.
 */
export function initializeAmplifyForDataResidency(requestedDataResidency: SupportedDataResidency): Promise<void> {
  const dataResidency = applyOAuthResidencyOverride(requestedDataResidency);

  const configured = configureChain
    // A failed configure must not poison the chain for later callers; it is
    // still propagated to the caller that asked for it, via `configured`.
    .catch(() => {})
    .then(() => configureAmplifyForDataResidency(dataResidency));

  configureChain = configured.catch(() => {});

  return configured;
}

async function configureAmplifyForDataResidency(dataResidency: SupportedDataResidency): Promise<void> {
  if (configuredResidency === dataResidency) {
    console.log(`✅ Amplify already configured for ${dataResidency}, skipping reconfiguration`);
    return;
  }

  try {
    const cognitoConfigModule = await dataResidencyManager.getCognitoConfiguration(dataResidency);
    const cognitoConfig: CognitoConfig =
      (cognitoConfigModule as { default?: CognitoConfig }).default || (cognitoConfigModule as unknown as CognitoConfig);

    const userPoolId = cognitoConfig.userPool || cognitoConfig.userPoolId;

    console.log(`🔧 Loading config for ${dataResidency}: region=${cognitoConfig.region}, userPool=${userPoolId}`);

    // Build OAuth callbacks if OAuth is configured
    const callbackUrlRoot = `${window.location.protocol}//${window.location.host}`;
    const signInCallback = `${callbackUrlRoot}${cognitoConfig.hostedUISignInCallbackPath || '/oauth/login'}`;
    const signOutCallback = `${callbackUrlRoot}${cognitoConfig.hostedUISignOutCallbackPath || '/'}`;

    if (cognitoConfig.hostedUICognitoDomain) {
      console.log(`🔧 OAuth callbacks - SignIn: ${signInCallback}, SignOut: ${signOutCallback}`);
    }

    // CROSS-APP CONTRACT: `cookieStorage` is what puts the Cognito tokens
    // somewhere a sibling app on the same origin can read them - peekapak.com
    // signs the student in, peekapak.com/play picks the session up. The
    // attributes (domain, path) and the resulting
    // `CognitoIdentityServiceProvider.<clientId>.*` cookie names are frozen;
    // changing them, or dropping the block so Amplify falls back to
    // localStorage, silently breaks the handoff for every peer build already
    // deployed. See DATA_RESIDENCY_STORAGE_KEY in residency.ts.
    const amplifyConfig: Record<string, unknown> = {
      Auth: {
        region: cognitoConfig.region, // AWS region for infrastructure
        userPoolId,
        userPoolWebClientId: cognitoConfig.clientId,
        identityPoolId: cognitoConfig.identityPool,
        mandatorySignIn: true,
        cookieStorage: {
          domain: window.location.hostname,
          path: '/',
          expires: 365,
          secure: window.location.protocol === 'https:',
          sameSite: 'strict',
        },
        authenticationFlowType: 'USER_SRP_AUTH',
      },
    };

    if (cognitoConfig.hostedUICognitoDomain) {
      amplifyConfig.oauth = {
        domain: cognitoConfig.hostedUICognitoDomain,
        scope: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: signInCallback,
        redirectSignOut: signOutCallback,
        responseType: 'code', // REFRESH token only generated when responseType is code
      };
      console.log('✅ OAuth configured for this data residency');
    } else {
      console.log('ℹ️ OAuth not available for this data residency');
      clearHostedUISignInMarker();
    }

    getDataResidencyRuntime().amplify.configure(amplifyConfig);

    configuredResidency = dataResidency;

    console.log(`✅ Amplify configured for ${dataResidency.toUpperCase()} data residency`);
  } catch (error) {
    console.error(`❌ Failed to initialize Amplify for ${dataResidency} data residency:`, error);
    throw error;
  }
}

/** The residency Amplify is presently configured for, or null before any call. */
export function getConfiguredDataResidency(): SupportedDataResidency | null {
  return configuredResidency;
}

/** Test seam: drop the configured-residency marker and the serialising chain. */
export function resetAmplifyConfigurationForTests(): void {
  configuredResidency = null;
  configureChain = Promise.resolve();
}

/**
 * Resolve the session's residency and configure Amplify for it. The single
 * entry point an app calls at startup.
 */
export async function initializeDataResidencyAwareAuth(): Promise<void> {
  try {
    await dataResidencyManager.initializeDataResidency();

    const currentDataResidency = dataResidencyManager.getCurrentDataResidency();
    console.log(`🌍 Detected user data residency: ${currentDataResidency.toUpperCase()}`);

    await initializeAmplifyForDataResidency(currentDataResidency);
  } catch (error) {
    console.error('❌ Failed to initialize data residency-aware authentication:', error);
    // Fall back to the default region rather than leaving Amplify unconfigured.
    await dataResidencyManager.setDataResidency(SupportedDataResidency.USA);
    await initializeAmplifyForDataResidency(SupportedDataResidency.USA);
  }
}
